/**
 * Reshaping a zone by hand: the geometry, kept apart from the mouse handling
 * so it can be tested without a map.
 *
 * Every function takes the shape as it was when the drag began and returns a
 * new zone. Applying each mousemove to the shape the previous mousemove made
 * would accumulate rounding, and a whole-zone move computed that way creeps
 * away from the cursor over a long drag.
 */

import { movePoint } from '@/map/useZoneLayer';
import { distanceM } from '@/map/zoneGeometry';
import type { Zone } from '@/stores/missionStore';

/** What a handle does when it is dragged. */
export type HandleRole = 'corner' | 'radius' | 'centre';

export interface ZoneHandle {
  role: HandleRole;
  /** Corner index for a polygon; 0 otherwise. */
  index: number;
  lat: number;
  lon: number;
  /** Drawn on the handle. Corners are numbered from 1, in ring order. */
  label: string;
}

/** The grab points of a zone. A circle's radius handle sits due east. */
export function zoneHandles(zone: Zone): ZoneHandle[] {
  if (zone.shape.type === 'circle') {
    const { lat, lon, radiusM } = zone.shape;
    const [edgeLon, edgeLat] = movePoint(lat, lon, 90, radiusM);
    return [
      { role: 'centre', index: 0, lat, lon, label: '' },
      { role: 'radius', index: 0, lat: edgeLat, lon: edgeLon, label: '' },
    ];
  }
  return zone.shape.points.map((point, index) => ({
    role: 'corner',
    index,
    lat: point.lat,
    lon: point.lon,
    label: String(index + 1),
  }));
}

export function moveCorner(zone: Zone, index: number, lat: number, lon: number): Zone {
  if (zone.shape.type !== 'polygon' || index < 0 || index >= zone.shape.points.length) return zone;
  const points = zone.shape.points.map((point, i) => (i === index ? { lat, lon } : point));
  return { ...zone, shape: { ...zone.shape, points } };
}

/** A circle's radius set by where the edge handle is, never below a metre. */
export function resizeCircleTo(zone: Zone, lat: number, lon: number): Zone {
  if (zone.shape.type !== 'circle') return zone;
  const radiusM = Math.max(1, Math.round(distanceM(zone.shape.lat, zone.shape.lon, lat, lon)));
  return { ...zone, shape: { ...zone.shape, radiusM } };
}

/**
 * The whole zone shifted by how far the cursor has travelled since the grab.
 *
 * A circle's centre travels with it. Moving only the outline and leaving the
 * centre behind is what made the predecessor's next resize snap the circle
 * back to where it had been.
 */
export function translateZone(zone: Zone, dLat: number, dLon: number): Zone {
  if (zone.shape.type === 'circle') {
    return {
      ...zone,
      shape: { ...zone.shape, lat: zone.shape.lat + dLat, lon: zone.shape.lon + dLon },
    };
  }
  const points = zone.shape.points.map((point) => ({
    lat: point.lat + dLat,
    lon: point.lon + dLon,
  }));
  return { ...zone, shape: { ...zone.shape, points } };
}

/** How near, in screen pixels, a click must land to count as the same point. */
export const SAME_POINT_PX = 8;

/**
 * What a click while drawing a polygon means, given where the existing corners
 * are on screen.
 *
 * "close" when it lands on the first corner of a ring that already has three,
 * which is the click the numbered first corner invites. "ignore" when it lands
 * on the corner just placed: that is the second click of a double-click, and a
 * duplicate corner is an extra fence vertex that skews the shape.
 */
export function polygonClick(
  click: { x: number; y: number },
  corners: ReadonlyArray<{ x: number; y: number }>,
): 'add' | 'close' | 'ignore' {
  const near = (point: { x: number; y: number } | undefined) =>
    point !== undefined && Math.hypot(point.x - click.x, point.y - click.y) <= SAME_POINT_PX;
  if (corners.length >= 3 && near(corners[0])) return 'close';
  if (near(corners[corners.length - 1])) return 'ignore';
  return 'add';
}
